// src/pages/om/GraphPage.js
// 运维中心 · 运维图谱（doc01 2.17，缺陷 105/106/107）
// 结构：头部「运维图谱」+ 右上「新建」→ 查询区（模糊搜索框 + 查询/重置）
//      → 表格（故障表现/可能原因/后果影响/检查步骤/处理方法/操作）。
// 操作：详情 / 编辑 / 删除（删除二次确认）；关联规则＝故障表现 ↔ 设备遥信多对多（仅取遥信、非必选、可多选，来源总告警库不分站点）。
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table, Input, Button, Space, Modal, Form, Select, Popconfirm, Tag, message,
} from 'antd';
import dayjs from 'dayjs';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';
import { OM_GRAPH_ROWS, OM_GRAPH_SIGNAL_TREE } from '../../config/stationData';

// 关联规则：自总告警库拉取全部遥信（仅取遥信），非必选、可多选
const SIGNAL_OPTIONS = OM_GRAPH_SIGNAL_TREE.flatMap((dev) => (dev.children || []).map((ch) => ({
  value: ch.key,
  label: `${dev.title} · ${ch.title}`,
})));
const SIGNAL_LABEL = SIGNAL_OPTIONS.reduce((a, o) => { a[o.value] = o.label; return a; }, {});

const KV = ({ items }) => (
  <div className="dev-kv">
    {items.map(([k, v]) => (
      <div className="dev-kv-row" key={k}>
        <span className="dev-kv-k">{k}</span>
        <span className="dev-kv-v">{v == null || v === '' ? '-' : v}</span>
      </div>
    ))}
  </div>
);

const GraphPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/om/graph');

  const [list, setList] = useState(() => OM_GRAPH_ROWS.map((g) => ({ ...g, signalKeys: [...(g.signalKeys || [])] })));
  const [draft, setDraft] = useState('');
  const [applied, setApplied] = useState('');
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const match = (g) => {
    if (!applied) return true;
    const kw = applied.trim().toLowerCase();
    return ['symptom', 'cause', 'impact', 'steps', 'solution']
      .some((k) => String(g[k] || '').toLowerCase().includes(kw));
  };

  const columns = [
    { title: '故障表现', dataIndex: 'symptom', width: 150, render: (v) => <span style={{ color: '#fff', fontWeight: 500 }}>{v}</span> },
    { title: '可能原因', dataIndex: 'cause', width: 180 },
    { title: '后果影响', dataIndex: 'impact', width: 230, render: (v) => v || <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span> },
    { title: '检查步骤', dataIndex: 'steps', width: 280, render: (v) => v || <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span> },
    { title: '处理方法', dataIndex: 'solution', width: 300 },
    {
      title: '操作', key: 'op', width: 180, fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          <Button type="link" size="small" onClick={() => setDetail(r)}>详情</Button>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm
            title="删除故障表现"
            description={`确认删除「${r.symptom}」？删除后该条目关联的遥信关系一并移除，且不可恢复。`}
            okText="删除" cancelText="取消" okButtonProps={{ danger: true }}
            onConfirm={() => removeRow(r)}
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ signalKeys: [] });
    setCreating(true);
  };

  function openEdit(r) {
    setEditing(r);
    form.setFieldsValue({
      symptom: r.symptom, cause: r.cause, impact: r.impact, steps: r.steps,
      solution: r.solution, signalKeys: r.signalKeys || [],
    });
    setCreating(true);
  }

  const submitForm = async () => {
    const v = await form.validateFields();
    if (editing) {
      setList((prev) => prev.map((g) => (g.key === editing.key ? { ...g, ...v } : g)));
      message.success('故障表现已更新');
    } else {
      const key = Date.now();
      setList((prev) => [{
        key, ...v, author: '当前用户', updated: dayjs().format('YYYY-MM-DD HH:mm'),
      }, ...prev]);
      message.success('故障表现已新增');
    }
    setCreating(false);
    form.resetFields();
    setEditing(null);
  };

  const removeRow = (r) => {
    setList((prev) => prev.filter((g) => g.key !== r.key));
    message.success('故障表现已删除');
  };

  const doQuery = () => { setApplied(draft); message.success('已按关键字搜索'); };
  const doReset = () => { setDraft(''); setApplied(''); message.success('搜索条件已重置'); };

  const actions = [{ key: 'new', label: '新建', primary: true }];
  const onAction = (a) => { if (a.key === 'new') openCreate(); };

  const d = detail || {};
  const signalTags = (d.signalKeys || []).map((k) => <Tag key={k} color="cyan" style={{ marginBottom: 4 }}>{SIGNAL_LABEL[k] || k}</Tag>);

  return (
    <>
      <PageSkeleton
        title={meta ? pick(meta.label, lang) : '运维图谱'}
        desc={meta ? pick(meta.desc, lang) : ''}
        crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
        filters={[]}
        actions={actions}
        onAction={onAction}
      >
        <div className="filter-bar">
          <div className="filter-bar-fields">
            <div className="filter-item">
              <Input
                allowClear
                style={{ width: 320 }}
                placeholder="请输入关键字（故障表现 / 原因 / 后果影响 / 处理方法）"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={doQuery}
              />
            </div>
          </div>
          <div className="filter-bar-actions">
            <Space>
              <Button type="primary" onClick={doQuery}>查询</Button>
              <Button onClick={doReset}>重置</Button>
            </Space>
          </div>
        </div>

        <Table
          rowKey="key"
          dataSource={list.filter(match)}
          columns={columns}
          pagination={{ pageSize: 10, size: 'small' }}
          size="middle"
          scroll={{ x: 1340 }}
        />
      </PageSkeleton>

      <Modal
        open={!!detail}
        title={`故障表现详情 · ${d.symptom || ''}`}
        width={760}
        onCancel={() => setDetail(null)}
        footer={<Button type="primary" onClick={() => setDetail(null)}>关闭</Button>}
      >
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-title">图谱条目</div>
          <KV items={[
            ['故障表现', d.symptom], ['可能原因', d.cause], ['后果影响', d.impact],
            ['检查步骤', d.steps], ['处理方法', d.solution],
            ['维护人', d.author], ['更新时间', d.updated],
          ]} />
        </div>
        <div className="panel">
          <div className="panel-title">关联规则 · 设备遥信<small>仅取遥信 · 非必选 · 可多选 · 总告警库不分站点</small></div>
          {signalTags.length ? <div>{signalTags}</div>
            : <span style={{ color: 'rgba(255,255,255,0.45)' }}>未关联任何遥信</span>}
        </div>
      </Modal>

      <Modal
        open={creating}
        title={editing ? '编辑故障表现' : '新增故障表现'}
        onCancel={() => { setCreating(false); setEditing(null); form.resetFields(); }}
        onOk={submitForm}
        okText={editing ? '保存' : '新增'}
        cancelText="取消"
        width={640}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="symptom" label="故障表现（模糊搜索键）" rules={[{ required: true, message: '请输入故障表现' }]}>
            <Input placeholder="如：BMS 过温" maxLength={40} />
          </Form.Item>
          <Form.Item name="cause" label="可能原因" rules={[{ required: true, message: '请输入可能原因' }]}>
            <Input placeholder="如：散热风道堵塞 / 空调失效" maxLength={80} />
          </Form.Item>
          <Form.Item name="impact" label="后果影响">
            <Input placeholder="如：可能导致 BMS 保护动作，储能系统停运" maxLength={80} />
          </Form.Item>
          <Form.Item name="steps" label="检查步骤" rules={[{ required: true, message: '请输入检查步骤' }]}>
            <Input placeholder="如：1) 查看堆温历史趋势；2) 检查空调运行与风道；3) 核对温度传感器采样" maxLength={160} />
          </Form.Item>
          <Form.Item name="solution" label="处理方法" rules={[{ required: true, message: '请输入处理方法' }]}>
            <Input.TextArea rows={2} maxLength={120} showCount />
          </Form.Item>
          <Form.Item
            name="signalKeys"
            label="关联遥信（仅取遥信，非必选，可多选）"
            tooltip="为建立故障运维指导与告警工单的联系，选择该故障表现对应某设备的哪些告警遥信；来源为总告警库，不区分站点"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="可多选，也可不选"
              optionFilterProp="label"
              options={SIGNAL_OPTIONS}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default GraphPage;
